import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseAPI } from '../config';
import { toast } from 'react-toastify';
import ExpedientNotesEditor from './ExpedientNotesEditor';
import {
  MANUALLY_SETTABLE_STATES,
  ALL_EXPEDIENT_TYPES,
  EXPEDIENT_TYPE_LABELS,
  expedientStatusLabel,
} from '@claimup/shared/constants/expedient';
import { normalizePaymentLink } from '@claimup/shared/helpers/payment-link';

function UpdateExpedient(props) {
  const [importe_deuda, setImporteDeuda] = useState(0);
  const [tipo_deuda, setTipoDeuda] = useState('');
  const [presupuesto, setPresupuesto] = useState(0);
  const [porcentajeBudget, setPorcentajeBudget] = useState(0);
  const [porcentajeDescuento, setPorcentajeDescuento] = useState(0);
  const [estado, setEstado] = useState('');
  // El estado con el que llegó: si es uno que solo pone el sistema (`Plan de
  // pagos`), se enseña para poder dejarlo, pero no se ofrece para elegirlo.
  const [estadoInicial, setEstadoInicial] = useState('');
  const [type, setType] = useState('');
  const [expedientId] = useState(props.idExpedient);
  const [aditionalExpedientData, setAditionalExpedientData] = useState('');
  const [clientInternalReference, setClientInternalReference] = useState('');
  const [divisa, setDivisa] = useState('EUR');
  const [paymentLink, setPaymentLink] = useState('');
  const [originalPaymentLink, setOriginalPaymentLink] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    axios
      .get(`${baseAPI}/expedients/` + expedientId, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      .then(response => {
        setImporteDeuda(response.data.importe_deuda);
        setTipoDeuda(response.data.tipo_deuda);
        setEstado(response.data.estado);
        setEstadoInicial(response.data.estado);
        setType(response.data.type || '');
        setPresupuesto(response.data.presupuesto);
        setPorcentajeBudget(response.data.porcentaje_presupuesto);
        setPorcentajeDescuento(response.data.porcentaje_descuento_deuda);
        setClientInternalReference(response.data.client_internal_reference);
        setAditionalExpedientData(response.data.aditional_expedient_data);
        setDivisa(response.data.divisa || 'EUR');
        setPaymentLink(response.data.url_pago_propia_cliente || '');
        setOriginalPaymentLink(response.data.url_pago_propia_cliente || '');
        setNotes(response.data.notes || '');
      })
      .catch(function (error) {
        console.log(error);
      });
  }, [expedientId]);

  const onChangeTipoDeuda = e => {
    setTipoDeuda(e.target.value);
  };

  const onChangeEstado = e => {
    setEstado(e.target.value);
  };

  const onChangeType = e => {
    setType(e.target.value);
  };

  const onChangePresupuesto = e => {
    setPresupuesto(e.target.value.replace(',', '.'));
  };

  const onChangePorcentajeBudget = e => {
    setPorcentajeBudget(e.target.value.replace(',', '.'));
  };

  const onChangePorcentajeDescuento = e => {
    setPorcentajeDescuento(e.target.value.replace(',', '.'));
  };

  const onChangeClientInternalReference = e => {
    setClientInternalReference(e.target.value);
  };

  const onChangeAditionalExpedientData = e => {
    setAditionalExpedientData(e.target.value);
  };

  const onChangeDivisa = e => {
    setDivisa(e.target.value);
  };

  const onChangePaymentLink = e => {
    setPaymentLink(e.target.value);
  };

  const onSubmit = e => {
    e.preventDefault();

    // Solo se manda si se ha tocado: un enlace antiguo que no sea http(s)
    // (la importadora no lo valida) no puede impedir guardar lo demás.
    // La regla es la misma que aplica el backend: vacío lo borra.
    const paymentLinkChanged = paymentLink !== originalPaymentLink;
    const link = normalizePaymentLink(paymentLink);
    if (paymentLinkChanged && link.error) {
      toast.error(link.error);
      return;
    }

    const expedient = {
      tipo_deuda: tipo_deuda,
      presupuesto: presupuesto,
      porcentaje_presupuesto: porcentajeBudget,
      estado: estado,
      type: type,
      porcentaje_descuento_deuda: porcentajeDescuento,
      client_internal_reference: clientInternalReference,
      aditional_expedient_data: aditionalExpedientData,
      divisa: divisa,
      ...(paymentLinkChanged && { url_pago_propia_cliente: link.value }),
    };

    async function updateExpedientId(expedient) {
      const response = await axios.post(`${baseAPI}/expedients/update/${expedientId}`, expedient, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response;
    }
    updateExpedientId(expedient).then(response => {
      if (response.status === 200) {
        toast.success('Expediente actualizado!', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }).catch(error => {
      // P. ej. 409 al poner en AR un expediente de una empresa sin AR.
      toast.error(error?.response?.data?.error || 'No se pudo actualizar el expediente');
    });
  };

  return (
    <div className='col-span-full xl:col-span-12 bg-white shadow-lg rounded-lg border border-gray-200'>
      <header className='px-5 py-4 border-b border-gray-100'>
        <h2 className='font-semibold text-gray-800'>Actualizar Expediente</h2>
      </header>

      <div className='p-5'>
        <form onSubmit={onSubmit} className='space-y-6'>
          <div className='bg-gray-50 border border-gray-200 rounded-lg p-6'>
            <h2 className='text-lg font-semibold text-gray-800 pb-3 border-b border-gray-200 mb-6'>
              Datos del Expediente
            </h2>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div className='md:col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='tipo-expediente'>
                  Tipo de expediente
                </label>
                <select
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='tipo-expediente'
                  onChange={onChangeType}
                  value={type}
                >
                  {ALL_EXPEDIENT_TYPES.map(t => (
                    <option key={t} value={t}>
                      {EXPEDIENT_TYPE_LABELS[t] || t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='importe-deuda'>
                  Importe de la Deuda
                </label>
                <div className='flex space-x-2'>
                  <input
                    className='flex-1 px-4 py-2 rounded-lg border border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed'
                    id='importe-deuda'
                    type='number'
                    placeholder='Deuda'
                    value={importe_deuda}
                    readOnly
                    disabled
                  />
                  <select
                    className='w-24 px-2 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                    value={divisa}
                    onChange={onChangeDivisa}
                    required={true}
                  >
                    <option value='EUR'>EUR</option>
                    <option value='USD'>USD</option>
                    <option value='GBP'>GBP</option>
                    <option value='JPY'>JPY</option>
                    <option value='CHF'>CHF</option>
                    <option value='AUD'>AUD</option>
                    <option value='CAD'>CAD</option>
                    <option value='CNY'>CNY</option>
                  </select>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='tipo-deuda'>
                  Tipo de deuda
                </label>
                <select
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='tipo-deuda'
                  onChange={onChangeTipoDeuda}
                  required={true}
                  value={tipo_deuda}
                >
                  <option>-Selecciona una opción-</option>
                  <option>Factura impagada</option>
                  <option>Reconocimiento de deuda</option>
                  <option>Préstamo personal sin garantía</option>
                  <option>Préstamo hipotecario</option>
                  <option>Pagaré</option>
                  <option>Tarjeta de crédito</option>
                  <option>Tarjeta revolving</option>
                  <option>Impago suscripción recurrente</option>
                  <option>Contrato de arrendamiento</option>
                  <option>Otro tipo de deuda</option>
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='presupuesto'>
                  Presupuesto fijo
                </label>
                <input
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='presupuesto'
                  type='number'
                  onChange={onChangePresupuesto}
                  value={presupuesto}
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='porcentaje-exito'>
                  % de éxito
                </label>
                <input
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='porcentaje-exito'
                  type='number'
                  placeholder='% de éxito'
                  onChange={onChangePorcentajeBudget}
                  value={porcentajeBudget}
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='referencia-cliente'>
                  Referencia cliente
                </label>
                <input
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='referencia-cliente'
                  type='text'
                  placeholder='Referencia cliente'
                  onChange={onChangeClientInternalReference}
                  value={clientInternalReference}
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='info-adicional'>
                  Información adicional
                </label>
                <input
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='info-adicional'
                  type='text'
                  placeholder='Información adicional'
                  onChange={onChangeAditionalExpedientData}
                  value={aditionalExpedientData}
                />
              </div>

              <div className='md:col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='enlace-pago'>
                  Enlace de pago del cliente
                </label>
                <input
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='enlace-pago'
                  type='text'
                  inputMode='url'
                  placeholder='https://… (vacío para quitarlo)'
                  onChange={onChangePaymentLink}
                  value={paymentLink}
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='estado'>
                  Estado
                </label>
                <select
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='estado'
                  onChange={onChangeEstado}
                  required={true}
                  value={estado}
                >
                  {(estadoInicial && !MANUALLY_SETTABLE_STATES.includes(estadoInicial)
                    ? [estadoInicial, ...MANUALLY_SETTABLE_STATES]
                    : MANUALLY_SETTABLE_STATES
                  ).map(status => (
                    <option key={status} value={status}>
                      {expedientStatusLabel(status)}
                    </option>
                  ))}
                </select>
                {/* Salir a mano de «Plan de pagos» no cancela el plan (docs/planes-de-pago-fase-1-plan.md). */}
                {estadoInicial === 'Plan de pagos' && estado !== estadoInicial && (
                  <p className='mt-2 text-sm text-amber-700'>
                    El plan de pagos sigue vigente. Si se ha caído, cancélalo en la pestaña «Plan de pagos».
                  </p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor='porcentaje-descuento'>
                  % de descuento deuda
                </label>
                <input
                  className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                  id='porcentaje-descuento'
                  type='number'
                  placeholder='% de descuento'
                  onChange={onChangePorcentajeDescuento}
                  value={porcentajeDescuento}
                />
              </div>
            </div>
          </div>

          <div className='flex justify-end'>
            <button
              className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              type='submit'
            >
              Actualizar Expediente
            </button>
          </div>
        </form>

        <h2 className='text-lg font-semibold text-gray-800 pb-3 border-b border-gray-200 mb-6'>Notas internas</h2>
        <ExpedientNotesEditor expedientId={expedientId} initialNotes={notes} />
      </div>
    </div>
  );
}

export default UpdateExpedient;
